// src/components/Hero.tsx
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext, type CarouselApi } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
// Keep local asset imports as fallbacks
import carouselImage1 from "@/assets/carousel-cake-1.jpg";
import carouselImage2 from "@/assets/carousel-cake-2.jpg";
import carouselImage3 from "@/assets/carousel-cake-3.jpg";
import carouselImage4 from "@/assets/carousel-cake-4.jpg";
import carouselImage5 from "@/assets/carousel-cake-5.jpg";
import carouselImage6 from "@/assets/carousel-cake-6.jpg";

/* --- Hero theme variable styles (scoped) --- */
const heroCss = `
  .hero-title      { color: var(--hero-title, #ffffff); }
  .hero-h1         { color: var(--hero-h1, #ffffff); }
  .hero-h2         { color: var(--hero-h2, #ffffff); }
  .hero-text       { color: var(--hero-h2, #ffffff); }

  .hero-btn--primary {
    background-color: var(--hero-btn-bg, #111111);
    color:            var(--hero-btn-text, #ffffff);
    border: 2px solid transparent;
    transition: background-color .2s ease, color .2s ease, border-color .2s ease;
  }
  .hero-btn--primary:hover {
    background-color: var(--hero-btn-hover-bg, #333333);
    color:            var(--hero-btn-hover-text, #ffffff);
  }

  /* Secondary keeps the translucent look but uses the same text/hover tokens */
  .hero-btn--secondary {
    background-color: color-mix(in srgb, var(--hero-btn-bg, #111111) 10%, transparent);
    color:            var(--hero-btn-text, #ffffff);
    border: 2px solid color-mix(in srgb, var(--hero-btn-text, #ffffff) 30%, transparent);
    backdrop-filter: blur(2px);
    transition: background-color .2s ease, color .2s ease, border-color .2s ease;
  }
  .hero-btn--secondary:hover {
    background-color: var(--hero-btn-hover-bg, #333333);
    color:            var(--hero-btn-hover-text, #ffffff);
    border-color:     var(--hero-btn-hover-bg, #333333);
  }
`;

// Define slides with their unique IDs for the API
const INITIAL_SLIDES_DATA = [
  {
    id: 'hero-1',
    src: carouselImage1,
    alt: "Jaw-dropping sculpted cake masterpiece",
    headline: "✨ YES, Art can be Edible! ✨",
    subheading: "Where imagination meets craftsmanship — luxury cakes sculpted into unforgettable works of art.",
    description: "At Frosted Cake Art, we transform imagination into breathtaking cakes — from sculpted masterpieces to elegant wedding designs. Each creation is a blend of artistry and flavor, crafted to leave a lasting impression.",
    buttons: [
      { text: "Explore Our Cakes", action: "gallery" },
      { text: "Request a Custom Design", action: "contact" }
    ]
  },
  {
    id: 'hero-2',
    src: carouselImage2,
    alt: "Sophisticated multi-tiered wedding cake with edible flowers",
    headline: "Edible Elegance for Your Forever Moments",
    subheading: "From timeless florals to bold statement designs, our wedding cakes make your celebration extraordinary.",
    description: "Every wedding deserves a centerpiece as unforgettable as your love story. Our elegant designs blend classic beauty with personalized touches.",
    buttons: [
      { text: "Explore Our Cakes", action: "gallery" },
      { text: "Start Your Wedding Cake Journey", action: "contact" }
    ]
  },
  {
    id: 'hero-3',
    src: carouselImage3,
    alt: "Whimsical sculpted cake with playful design",
    headline: "Anything Can Become Cake",
    subheading: "From whimsical to larger-than-life, we sculpt cakes that surprise, delight, and wow every guest.",
    description: "Dream it, and we'll sculpt it. Our artistic creations push the boundaries of what's possible in cake design.",
    buttons: [
      { text: "Explore Our Cakes", action: "gallery" },
      { text: "Request a Custom Design", action: "contact" }
    ]
  },
  {
    id: 'hero-4',
    src: carouselImage4,
    alt: "Close-up of fine edible detailing with sugar flowers and gold leaf",
    headline: "Crafted with Artistry. Designed to Impress.",
    subheading: "Every cake is a fusion of culinary mastery and artistic vision, tailored to your story.",
    description: "Fine details and luxurious finishes transform each cake into a work of art that tastes as extraordinary as it looks.",
    buttons: [
      { text: "Explore Signature Styles", action: "gallery" },
      { text: "Request a Custom Design", action: "contact" }
    ]
  }
];


export const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [api, setApi] = useState<CarouselApi>();
  // NEW STATE: Stores the map of live URLs from the API
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});

  // NEW EFFECT: Fetch all hero image URLs on load
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const ids = INITIAL_SLIDES_DATA.map(s => s.id);
        // Call the new general API with all Hero IDs
        const r = await fetch(`/api/site-image?ids=${ids.join(',')}`, { method: 'GET' });
        const j = await r.json();

        if (alive && j?.ok && j.urls) {
          // Store fetched URLs { 'hero-1': 'https://...', 'hero-2': 'https://...', ... }
          setImageUrls(j.urls);
        }
      } catch { /* ignore and use local defaults */ }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!api) {
      return;
    }

    api.on("select", () => {
      setCurrentSlide(api.selectedScrollSnap());
    });
  }, [api]);

  const scrollToSection = (section: string) => {
    const element = document.querySelector(`#${section}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleButtonClick = (action: string) => {
    scrollToSection(action);
  };
  
  // MAPPED SLIDES: Use the dynamic URL if available, otherwise the local asset
  const dynamicSlides = INITIAL_SLIDES_DATA.map((slide) => ({
      ...slide,
      src: imageUrls[slide.id] || slide.src // Dynamic URL or local asset fallback
  }));


  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <style>{heroCss}</style>
      <div className="absolute inset-0">
        <Carousel
          setApi={setApi}
          className="w-full h-full"
          plugins={[
            Autoplay({
              delay: 5000,
            }),
          ]}
          opts={{
            align: "start",
            loop: true,
          }}
        >
          <CarouselContent className="h-screen">
            {dynamicSlides.map((slide, index) => ( // <-- USE dynamicSlides HERE
              <CarouselItem key={index} className="h-full relative">
                <img
                  src={slide.src} // <-- DYNAMIC SOURCE
                  alt={slide.alt}
                  className="w-full h-full object-cover"
                />
                {/* Soft vignette overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-black/20" />
                <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-black/40" />
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 border-white/20 text-white hover:bg-black/70 z-20" />
          <CarouselNext className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 border-white/20 text-white hover:bg-black/70 z-20" />
        </Carousel>
      </div>
      
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="max-w-4xl mx-auto">
          {/* Dynamic content based on current slide */}
          <h1 className="hero-h1 mb-6 leading-tight drop-shadow-lg">
            {dynamicSlides[currentSlide]?.headline}
          </h1>
          
          <p className="hero-h2 subheading mb-4 max-w-2xl mx-auto drop-shadow-md">
            {dynamicSlides[currentSlide]?.subheading?.toUpperCase()}
          </p>
          
          <p className="hero-text text-base sm:text-lg mb-8 leading-relaxed max-w-3xl mx-auto">
            {dynamicSlides[currentSlide]?.description}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {dynamicSlides[currentSlide]?.buttons.map((button, index) => (
              <Button
                key={index}
                onClick={() => handleButtonClick(button.action)}
                size="lg"
                className={`
                  px-8 py-4 text-lg font-semibold rounded-xl transition-all duration-300 elegant-shadow
                  ${index === 0 ? "hero-btn--primary" : "hero-btn--secondary"}
                `}
              >
                {button.text}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};